import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/spaces` (см. `docs/api-design.md §4.3-4.4`,
 * `docs/REQUIREMENTS.md FR-1`, `docs/data-model.md §3.2`).
 *
 * Пространства — top-level контейнер, роль пользователя per-space
 * (`admin` / `member` / `viewer`). Инвариант `role ∈ {...}` обеспечен
 * `CHECK`-constraint'ом в миграции M2 (data-model.md §5).
 */
// ─── Роль в пространстве ──────────────────────────────────────────────────
export const SPACE_ROLES = ['admin', 'member', 'viewer'];
export const SpaceRoleSchema = z.enum(SPACE_ROLES);
// ─── Slug ────────────────────────────────────────────────────────────────
//
// URL-safe идентификатор пространства: строчные латинские, цифры и дефис.
// 3..50 символов, не может начинаться/заканчиваться дефисом.
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
// ─── Space DTO ───────────────────────────────────────────────────────────
//
// Форма пространства, которую API возвращает на фронт. `myRole`
// вычисляется в хендлере по `(spaceId, currentUserId)`; для
// платформ-админов, не являющихся членами, значение остаётся `'admin'`
// (platform_admin имеет полные права согласно auth.md §4.2).
export const SpaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    slug: z.string().regex(SLUG_RE),
    description: z.string().max(1000).nullable(),
    icon: z.string().max(48).nullable().optional(),
    isArchived: z.boolean(),
    /**
     * Открытый доступ: пространство видно ВСЕМ пользователям без явного
     * добавления в участники. Пользователь без записи в `space_members`
     * получает эффективную роль `member` (участник). Явное членство и
     * platform-admin имеют приоритет над этим флагом.
     */
    publicAccess: z.boolean(),
    jiraIntegrationEnabled: z.boolean(),
    hermesIntegrationEnabled: z.boolean(),
    /** Общий контекст, отправляемый в Hermes со ВСЕМИ задачами (преамбула к описанию). */
    hermesCommonContext: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    myRole: SpaceRoleSchema,
});
// ─── POST /api/spaces ────────────────────────────────────────────────────
export const CreateSpaceRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    slug: z.string().trim().regex(SLUG_RE),
    description: z.string().max(1000).optional(),
    icon: z.string().max(48).optional(),
});
// ─── PATCH /api/spaces/:id ───────────────────────────────────────────────
//
// Slug неизменяем после создания (см. FR-1.3): меняем только name/description.
export const UpdateSpaceRequestSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(1000).nullable().optional(),
    icon: z.string().max(48).nullable().optional(),
    publicAccess: z.boolean().optional(),
    jiraIntegrationEnabled: z.boolean().optional(),
    hermesIntegrationEnabled: z.boolean().optional(),
    hermesCommonContext: z.string().max(5000).nullable().optional(),
});
// ─── GET /api/spaces ─────────────────────────────────────────────────────
//
// Две секции по FR-1.2: `mine` — пространства, где пользователь
// участник (любая роль); `available` — всё остальное, видимое ему
// (в MVP это значит: непустой список только для платформ-админов).
export const SpaceListResponseSchema = z.object({
    mine: z.array(SpaceSchema),
    available: z.array(SpaceSchema),
});
// ─── Откуда у человека доступ ────────────────────────────────────────────
//
// `manual` — админ добавил его руками; `group` — он состоит в группе
// Keycloak, привязанной к пространству. Групповые строки пересчитывает
// синхронизация: роль у них меняют через роль группы, а убирают человека —
// в Keycloak либо отвязав группу.
export const MEMBER_SOURCES = ['manual', 'group'];
export const MemberSourceSchema = z.enum(MEMBER_SOURCES);
/** Короткая ссылка на группу, из-за которой у человека есть доступ. */
export const MemberSourceGroupSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Путь привязанной группы. */
    path: z.string(),
    /**
     * Путь группы, где человек состоит на самом деле. У привязки `/collection`
     * это может быть `/collection/BA`: привязка покрывает всё поддерево.
     * Совпадает с `path`, если человек состоит в самой привязанной группе.
     */
    memberPath: z.string().nullable().default(null),
});
// ─── SpaceMember DTO ─────────────────────────────────────────────────────
export const SpaceMemberSchema = z.object({
    userId: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    avatarUrl: z.string().nullable().default(null),
    role: SpaceRoleSchema,
    source: MemberSourceSchema.default('manual'),
    sourceGroup: MemberSourceGroupSchema.nullable().default(null),
});
export const SpaceMemberListResponseSchema = z.object({
    members: z.array(SpaceMemberSchema),
});
// ─── GET /api/spaces/:id/assignable-users ────────────────────────────────
//
// Лёгкий справочник активных пользователей платформы для пикера
// «Добавить участников». Доступен space-АДМИНУ, а не только
// platform-админу — в отличие от `/api/admin/users` (заперт за
// `requirePlatformAdmin`). Раньше пикер дёргал именно админский эндпоинт,
// поэтому у space-админа без платформенных прав он отдавал 403 и список
// был пуст («Нет данных»). Уже состоящих в пространстве фронт
// отфильтровывает сам по `members`.
export const AssignableUserSchema = z.object({
    id: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
});
export const AssignableUserListResponseSchema = z.object({
    users: z.array(AssignableUserSchema),
});
// ─── POST /api/spaces/:id/members ────────────────────────────────────────
export const AddSpaceMemberRequestSchema = z.object({
    userId: z.string().uuid(),
    role: SpaceRoleSchema,
});
// ─── POST /api/spaces/:id/members/bulk ───────────────────────────────────
//
// Массовое добавление сразу нескольких пользователей с одной ролью на всех.
// Дубликаты в `userIds` схлопываются на сервере; уже состоящие в
// пространстве и несуществующие пользователи не роняют запрос, а
// возвращаются в `skipped` с причиной.
// `keycloakIds` — люди, найденные в Keycloak, но ещё не заведённые в Lens
// (они не входили ни разу). Сервер сам заводит им запись с привязкой к
// аккаунту Keycloak и добавляет в пространство. Хотя бы один из двух списков
// должен быть непустым.
export const BulkAddSpaceMembersRequestSchema = z
    .object({
    userIds: z.array(z.string().uuid()).max(500).optional(),
    keycloakIds: z.array(z.string().min(1)).max(500).optional(),
    role: SpaceRoleSchema,
})
    .refine((v) => (v.userIds?.length ?? 0) + (v.keycloakIds?.length ?? 0) > 0, {
    message: 'Выберите хотя бы одного пользователя',
    path: ['userIds'],
});
export const BulkAddSkipReasonSchema = z.enum([
    'already_member',
    'user_not_found',
    'user_disabled',
    'keycloak_unavailable',
]);
export const BulkAddSpaceMembersResponseSchema = z.object({
    added: z.array(SpaceMemberSchema),
    skipped: z.array(z.object({
        /** id в Lens — либо, для не заведённых ещё людей, id в Keycloak. */
        userId: z.string(),
        login: z.string().nullable().default(null),
        reason: BulkAddSkipReasonSchema,
    })),
    /** Сколько человек пришлось завести в Lens перед добавлением. */
    createdUsers: z.number().int().nonnegative().default(0),
});
// ─── PATCH /api/spaces/:id/members/:userId ───────────────────────────────
export const UpdateSpaceMemberRequestSchema = z.object({
    role: SpaceRoleSchema,
});
// ─── Группы Keycloak, привязанные к пространству ─────────────────────────
//
// Привязка постоянная: состав пересчитывается при входе человека в Lens и по
// кнопке «Обновить». Кого добавили в группу позже — получает доступ сам,
// кого убрали — теряет. Роль у всех членов группы одна.
export const SpaceGroupSchema = z.object({
    id: z.string().uuid(),
    keycloakId: z.string(),
    /** Путь в дереве групп Keycloak, например `/ДРЦС/Мобильные`. */
    path: z.string(),
    name: z.string(),
    role: SpaceRoleSchema,
    /** Сколько человек получили доступ по этой группе на момент пересчёта. */
    memberCount: z.number().int().nonnegative(),
    lastSyncedAt: z.string().nullable(),
    /** Чем закончился последний неудачный пересчёт (null, если всё хорошо). */
    lastSyncError: z.string().nullable(),
    createdAt: z.string(),
});
export const SpaceGroupListResponseSchema = z.object({
    groups: z.array(SpaceGroupSchema),
    /** Настроен ли вход через Keycloak — без него привязывать группы нечем. */
    keycloakConfigured: z.boolean(),
});
// ─── POST /api/spaces/:id/groups ─────────────────────────────────────────
//
// Привязывает сразу несколько групп с одной ролью. Имя и путь сервер
// перечитывает из Keycloak по id — клиенту их подделать нельзя.
export const LinkSpaceGroupsRequestSchema = z.object({
    keycloakIds: z.array(z.string().min(1)).min(1).max(50),
    role: SpaceRoleSchema,
});
// ─── PATCH /api/spaces/:id/groups/:groupId ───────────────────────────────
export const UpdateSpaceGroupRequestSchema = z.object({
    role: SpaceRoleSchema,
});
// ─── Итог пересчёта состава ──────────────────────────────────────────────
export const SpaceGroupSyncSummarySchema = z.object({
    /** Заведено записей в Lens для тех, кто ни разу не входил. */
    usersCreated: z.number().int().nonnegative(),
    /** Появилось новых участников. */
    added: z.number().int().nonnegative(),
    /** Сменилась роль у уже существующих. */
    updated: z.number().int().nonnegative(),
    /** Убрано — вышли из группы или группу отвязали. */
    removed: z.number().int().nonnegative(),
    /** Что помешало (группа не найдена, последний администратор и т.п.). */
    warnings: z.array(z.string()),
});
export const SpaceGroupSyncResponseSchema = z.object({
    groups: z.array(SpaceGroupSchema),
    members: z.array(SpaceMemberSchema),
    summary: SpaceGroupSyncSummarySchema,
});
// ─── GET /api/spaces/:id/keycloak/groups?search= ─────────────────────────
//
// Поиск групп для пикера. Доступен админу пространства (не только
// платформенному) — привязка групп это часть настроек пространства.
export const KeycloakGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    path: z.string(),
    /** Уже привязана к этому пространству. */
    linked: z.boolean(),
    /**
     * Есть ли подгруппы. `null` — Keycloak не сказал (старые версии), тогда
     * пикер всё равно даёт раскрыть строку и показывает результат.
     */
    hasChildren: z.boolean().nullable().default(null),
});
export const KeycloakGroupListResponseSchema = z.object({
    groups: z.array(KeycloakGroupSchema),
    configured: z.boolean(),
});
// ─── GET /api/spaces/:id/keycloak/users?search= ──────────────────────────
//
// Люди из Keycloak для пикера — включая тех, кого в Lens ещё нет
// (`lensUserId: null`). Их запись заводится в момент добавления.
export const KeycloakDirectoryUserSchema = z.object({
    keycloakId: z.string(),
    login: z.string(),
    displayName: z.string(),
    email: z.string().nullable(),
    enabled: z.boolean(),
    lensUserId: z.string().uuid().nullable(),
    /** Уже участник этого пространства. */
    isMember: z.boolean(),
});
export const KeycloakUserSearchResponseSchema = z.object({
    users: z.array(KeycloakDirectoryUserSchema),
    configured: z.boolean(),
});
// ─── Группы пользователей внутри пространства ────────────────────────────────────
//
// Набор участников под общим названием («Команда Android»). Нужен, чтобы
// заполнять поля типа `user` одним выбором: в выпадающем списке поля есть
// раздел с группами, выбор группы подставляет её участников по отдельности.
// В записи хранятся пользователи, а не группа, — поэтому изменение состава группы
// задним числом уже заполненные поля не трогает.
//
// К доступу отношения не имеет: это не `SpaceGroup` (группа Keycloak, которая
// раздаёт роли). Состав ведут руками админы пространства.
export const SpaceUserGroupMemberSchema = z.object({
    userId: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
    avatarUrl: z.string().nullable().default(null),
});
export const SpaceUserGroupSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    members: z.array(SpaceUserGroupMemberSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SpaceUserGroupListResponseSchema = z.object({
    groups: z.array(SpaceUserGroupSchema),
});
export const CreateSpaceUserGroupRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    userIds: z.array(z.string().uuid()).max(500).optional().default([]),
});
// Все поля необязательны: переименование, смена описания и правка состава —
// независимые операции. `userIds` задаёт состав целиком (не добавляет).
export const UpdateSpaceUserGroupRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    userIds: z.array(z.string().uuid()).max(500).optional(),
})
    .refine((v) => v.name !== undefined || v.description !== undefined || v.userIds !== undefined, {
    message: 'Нечего менять',
});
// ─── UserSpace (для /api/auth/me.spaces) ─────────────────────────────────
//
// Минимальный slice пространства, нужный топбару/боковому меню без
// обращения к `/api/spaces`. Контракт сознательно отличается от
// `SpaceSchema` — фронту на главной нужны только id/name/slug/role.
export const UserSpaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    icon: z.string().nullable().optional(),
    role: SpaceRoleSchema,
});
// ─── Коды ошибок модуля spaces ───────────────────────────────────────────
export const SPACES_ERROR_CODES = [
    'validation_error',
    'unauthorized',
    'forbidden',
    'not_found',
    'slug_conflict',
    'cannot_delete_active_space',
    'cannot_remove_last_admin',
    'user_already_member',
    // Доступ по группе Keycloak
    'sso_not_configured',
    'keycloak_unavailable',
    'group_not_found',
    'group_already_linked',
    'member_managed_by_group',
    // Группы пользователей внутри пространства
    'user_group_name_conflict',
    'user_group_member_not_in_space',
];
// ─── M12: Space Templates ───────────────────────────────────────────────────
export const SpaceTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    description: z.string().nullable(),
    isBuiltin: z.boolean(),
    config: z.record(z.unknown()),
    createdById: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SpaceTemplateListResponseSchema = z.object({
    templates: z.array(SpaceTemplateSchema),
});
// Extend CreateSpaceRequest with optional templateId
export const CreateSpaceFromTemplateRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    slug: z.string().trim().regex(SLUG_RE),
    description: z.string().max(1000).optional(),
    icon: z.string().max(48).optional(),
    templateId: z.string().uuid().optional(),
});
// M17: сохранение существующего пространства как пользовательского шаблона.
export const CreateTemplateFromSpaceRequestSchema = z.object({
    spaceId: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).optional(),
});
